import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Film, Send, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const params = new URLSearchParams(useLocation().search);
  const [isLogin, setIsLogin] = useState(params.get('signup') !== '1');
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (forgot) {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
      } else if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
      {/* Background */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920&q=80)',
          backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(6px)'
        }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#11041F] via-[#11041F]/80 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6 animate-scaleIn">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img src={LOGO_URL} alt="Maya+" className="h-14" data-testid="auth-logo" />
        </div>

        <div className="bg-[#1a0e2e]/80 backdrop-blur-xl rounded-xl p-8 border border-white/5">
          <h1 className="text-2xl font-bold text-center mb-2" data-testid="auth-title">
            {forgot ? 'Mot de passe oublié' : isLogin ? 'Connexion' : 'Créer un compte'}
          </h1>
          <p className="text-[#A1A1AA] text-sm text-center mb-8">
            {forgot ? "Indiquez l'email de votre compte, nous vous enverrons un lien sécurisé." : isLogin ? 'Accédez à votre espace Maya+' : 'Rejoignez la communauté Maya+'}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 rounded-lg mb-4" data-testid="auth-error">
              {error}
            </div>
          )}

          {forgotSent && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm p-3 rounded-lg mb-4" data-testid="forgot-sent-msg">
              Si un compte existe avec cet email, un lien de réinitialisation vient de vous être envoyé. Pensez à vérifier vos spams.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && !forgot && (
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">Nom</label>
                <input
                  data-testid="auth-name-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A] transition-colors"
                  placeholder="Votre nom"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1.5">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A] transition-colors"
                placeholder="votre@email.com"
                required
              />
            </div>
            {!forgot && (
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1.5">Mot de passe</label>
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 pr-12 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A] transition-colors"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {forgot ? <Send size={17} /> : <Film size={18} />}
                  {forgot ? 'Envoyer le lien de réinitialisation' : isLogin ? 'Se connecter' : "S'inscrire"}
                </>
              )}
            </button>
          </form>

          {isLogin && !forgot && (
            <div className="mt-4 text-center">
              <button data-testid="auth-forgot-link" onClick={() => { setForgot(true); setError(''); setForgotSent(false); }}
                className="text-[#A1A1AA] text-sm hover:text-white transition-colors">
                Mot de passe <span className="text-[#E9641A] font-semibold">oublié ?</span>
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            {forgot ? (
              <button data-testid="auth-back-login" onClick={() => { setForgot(false); setForgotSent(false); setError(''); }}
                className="text-[#A1A1AA] text-sm hover:text-white transition-colors inline-flex items-center gap-1.5">
                <ArrowLeft size={13} /> Retour à la <span className="text-[#E9641A] font-semibold">connexion</span>
              </button>
            ) : (
            <button
              data-testid="auth-toggle-mode"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-[#A1A1AA] text-sm hover:text-white transition-colors"
            >
              {isLogin ? "Pas de compte ? " : "Déjà un compte ? "}
              <span className="text-[#E9641A] font-semibold">
                {isLogin ? "S'inscrire" : "Se connecter"}
              </span>
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
